// @ts-check
/** One wholesale partner: overview, message timeline, quotes, agreement and placements. */
import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import LoadingScreen from '../components/LoadingScreen';
import AgreementTab from '../components/wholesale/AgreementTab';
import OverviewTab from '../components/wholesale/OverviewTab';
import PlacementsTab from '../components/wholesale/PlacementsTab';
import QuotesTab from '../components/wholesale/QuotesTab';
import { StageBadge } from '../components/wholesale/shared';
import TimelineTab from '../components/wholesale/TimelineTab';
import { useToast } from '../context/ToastContext';
import adminWholesaleService from '../services/adminWholesaleService';
import { formatDay, label } from '../utils/wholesale';

const WholesalePartnerPage = () => {
  const { partnerId } = useParams();
  const toast = useToast();
  const [partner, setPartner] = useState(null);
  const [tab, setTab] = useState('overview');
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      setPartner(await adminWholesaleService.getPartner(partnerId));
    } catch (err) {
      setNotFound(true);
      toast.error(err.message || 'Failed to load the partner');
    }
  }, [partnerId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  if (notFound) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground mb-4">That partner could not be found.</p>
        <Link to="/wholesale" className="underline">
          Back to Wholesale
        </Link>
      </div>
    );
  }
  if (!partner) return <LoadingScreen />;

  const hasActiveAgreement = partner.agreements.some((a) => a.status === 'active');
  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'timeline', label: 'Timeline' },
    { key: 'quotes', label: 'Quotes' },
    { key: 'agreement', label: 'Agreement' },
    ...(hasActiveAgreement ? [{ key: 'placements', label: 'Placements' }] : []),
  ];

  return (
    <div>
      <Link to="/wholesale" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft size={14} /> Wholesale
      </Link>
      <div className="flex flex-wrap items-center gap-3 mb-1">
        <h1 className="text-3xl font-serif">{partner.name}</h1>
        <StageBadge stage={partner.stage} />
        {partner.is_archived && <span className="text-xs px-2 py-0.5 rounded-md bg-muted text-muted-foreground">Archived</span>}
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        {[partner.city, label(partner.kind), `${label(partner.price_tier)} tier`].filter(Boolean).join(' · ')}
        {partner.next_action ? ` · Next: ${partner.next_action}${partner.next_action_on ? ` (${formatDay(partner.next_action_on)})` : ''}` : ''}
      </p>

      <div className="flex gap-1 bg-muted rounded-lg p-1 mb-6 max-w-2xl overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-shrink-0 whitespace-nowrap flex-1 px-4 py-2 text-sm font-medium rounded-lg transition ${
              tab === t.key ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab partner={partner} onChanged={load} />}
      {tab === 'timeline' && <TimelineTab partner={partner} onChanged={load} />}
      {tab === 'quotes' && <QuotesTab partner={partner} onChanged={load} />}
      {tab === 'agreement' && <AgreementTab partner={partner} onChanged={load} />}
      {tab === 'placements' && hasActiveAgreement && <PlacementsTab partner={partner} />}
    </div>
  );
};

export default WholesalePartnerPage;
